<template>
  <div>
    <LabelledInput label="Collapse Options">
      <b-checkbox v-model="collapsable">
        Collapsable
      </b-checkbox>

      <b-checkbox
        v-if="collapsable"
        v-model="startCollapsed"
      >
        Start Collapsed
      </b-checkbox>

      <b-input-group
        v-if="collapsable"
        prepend="Button text"
      >
        <b-input
          v-model="collapseButtonText"
          type="text"
          placeholder="Summary Table"
        />
      </b-input-group>
    </LabelledInput>

    <LabelledInput label="Child Table Options">
      <b-checkbox v-model="displayAutoTotal">
        Display total
      </b-checkbox>

      <b-checkbox v-model="displayHeaders">
        Display headers
      </b-checkbox>

      <b-checkbox
        v-if="displayHeaders"
        v-model="allowCustomSort"
      >
        Allow custom sorting
        <b-button
          v-if="allowCustomSort"
          variant="link"
          class="p-0"
          style="vertical-align: baseline;"
          @click="clearCustomSort"
        >
          (reset)
        </b-button>
      </b-checkbox>

      <b-checkbox v-model="sortTabOrder">
        Display in tab order (root & subroot only)
      </b-checkbox>

      <b-radio-group
        v-model="sortBy"
        :options="sortByOptions"
      />

      <b-radio-group
        v-model="sortDesc"
        :options="sortDescOptions"
      />
    </LabelledInput>
  </div>
</template>

<script lang="ts">

import Vue from 'vue'
import Component from 'vue-class-component'

import LabelledInput from '@/components/builder/UI/LabelledInput.vue'
import { SummaryTableConfig } from '@/types'
import { SUMMARY_TABLE_CONFIG_KEY } from '@/constants'

const components = {
  LabelledInput,
}

@Component({ components })
export default class SelectOptions extends Vue {
  sortByOptions = [
    { text: 'Sort by name', value: 'name' },
    { text: 'Sort by value', value: 'value' },
  ]

  sortDescOptions = [
    { text: 'Sort ascending', value: false },
    { text: 'Sort descending', value: true },
  ]

  get collapsable () {
    return this.$typedStore.primary.app.config.summaryTable.collapsable
  }

  set collapsable (value) {
    this.updateSummaryTableConfig(value, 'collapsable')
  }

  get collapseButtonText () {
    return this.$typedStore.primary.app.config.summaryTable.collapseButtonText
  }

  set collapseButtonText (value) {
    this.updateSummaryTableConfig(value, 'collapseButtonText')
  }

  get displayAutoTotal () {
    return this.$typedStore.primary.app.config.summaryTable.displayAutoTotal
  }

  set displayAutoTotal (value) {
    this.updateSummaryTableConfig(value, 'displayAutoTotal')
  }

  get startCollapsed () {
    return this.$typedStore.primary.app.config.summaryTable.startCollapsed
  }

  set startCollapsed (value) {
    this.updateSummaryTableConfig(value, 'startCollapsed')
  }

  get displayHeaders () {
    return this.$typedStore.primary.app.config.summaryTable.displayHeaders
  }

  set displayHeaders (value) {
    this.updateSummaryTableConfig(value, 'displayHeaders')
  }

  get allowCustomSort () {
    return this.$typedStore.primary.app.config.summaryTable.allowCustomSort
  }

  set allowCustomSort (value) {
    this.updateSummaryTableConfig(value, 'allowCustomSort')
  }

  get sortBy () {
    return this.$typedStore.primary.app.config.summaryTable.sortBy
  }

  set sortBy (value) {
    this.updateSummaryTableConfig(value, 'sortBy')
  }

  get sortDesc () {
    return this.$typedStore.primary.app.config.summaryTable.sortDesc
  }

  set sortDesc (value) {
    this.updateSummaryTableConfig(value, 'sortDesc')
  }

  get sortTabOrder () {
    return this.$typedStore.primary.app.config.summaryTable.sortTabOrder
  }

  set sortTabOrder (value) {
    this.updateSummaryTableConfig(value, 'sortTabOrder')
  }

  updateSummaryTableConfig (value: unknown, ...args: [keyof SummaryTableConfig]) {
    this.$typedStore.primary.app.updateConfig({
      configPath: [SUMMARY_TABLE_CONFIG_KEY, ...args],
      value,
    })
  }

  async clearCustomSort () {
    const sortBy = this.sortBy
    const sortDesc = this.sortDesc
    this.sortBy = null
    this.sortDesc = false
    await this.$nextTick()
    this.sortBy = sortBy
    this.sortDesc = sortDesc
  }
}

</script>
